import { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import { Controller } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf,
  faXmark,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import type { FileMemo } from 'models/Memo';
import TitleField from '../fields/TitleField';
import { useTranslations } from 'next-intl';
import { useMemoFormContext } from '../context/FormContext';
import { useMediaUpload, type UploadItem } from '../hooks/useMediaUpload';
import useMedia from 'networking/mindfeed/hooks/useMedia';
import { ACCEPTED_FILE_TYPES, validateFiles } from './fileValidation';
import { FileTypeBadge } from 'elements/Media';

interface Props {
  memo?: FileMemo | null;
}

const FileForm: FC<Props> = ({ memo }) => {
  const { control, isTitleVisible } = useMemoFormContext();
  const t = useTranslations();
  const { selectedBox } = useActiveSpace();
  const boxId = memo?.boxId ?? selectedBox?.id;

  const { items, addFiles, removeItem } = useMediaUpload({
    name: 'fileIds',
    boxId,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickFiles = () => inputRef.current?.click();

  const onSelected = (files: FileList | null) => {
    const { accepted, errors } = validateFiles(Array.from(files ?? []));
    setError(errors.length ? errors.join('\n') : null);
    if (accepted.length) addFiles(accepted);
  };

  return (
    <div className="space-y-3">
      {isTitleVisible && <TitleField />}
      <Controller
        control={control}
        name="content"
        render={({ field: { onChange, value, onBlur } }) => (
          <TextareaAutosize
            minRows={1}
            maxRows={5}
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            className={styles.caption}
            placeholder={t('memoComposer.descriptionPlaceholder')}
          />
        )}
      />

      {items.length === 0 ? (
        <button
          type="button"
          onClick={pickFiles}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            onSelected(e.dataTransfer.files);
          }}
          className={classNames(styles.dropzone, isDragging && styles.dragging)}
        >
          <FontAwesomeIcon icon={faFilePdf} className={styles.dropIcon} />
          <span className={styles.dropTitle}>{t('memoComposer.file.add')}</span>
          <span className={styles.dropHint}>{t('memoComposer.file.hint')}</span>
        </button>
      ) : (
        <ul className={styles.list}>
          {items.map((item) => (
            <FileItem
              key={item.key}
              item={item}
              onRemove={() => removeItem(item.key)}
            />
          ))}

          <button type="button" onClick={pickFiles} className={styles.addMore}>
            {t('memoComposer.file.addAnother')}
          </button>
        </ul>
      )}

      {error && <p className={styles.validationError}>{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(',')}
        multiple
        hidden
        onChange={(e) => {
          onSelected(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default FileForm;

// One file in the list. A freshly picked file knows its name; an attachment
// that already belongs to the memo only has a media id, so its name comes
// from the media lookup, like on the memo card.
const FileItem: FC<{ item: UploadItem; onRemove: () => void }> = ({
  item,
  onRemove,
}) => {
  const t = useTranslations();
  const { data: asset } = useMedia(item.name ? undefined : item.mediaId);
  const name = item.name ?? asset?.fileName ?? t('memoCard.file.document');

  return (
    <li className={styles.item}>
      <FileTypeBadge fileName={name} size={30} className="shrink-0" />
      <span className={styles.name}>{name}</span>

      {item.status === 'uploading' && (
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-ink-tertiary"
          aria-label={t('memoComposer.uploading')}
        />
      )}
      {item.status === 'error' && (
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="text-mf-red-600"
          aria-label={t('memoComposer.uploadFailed')}
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('memoComposer.file.remove')}
        className={styles.remove}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </li>
  );
};

const styles = {
  dropzone: classNames(
    'flex w-full flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed',
    'border-stroke bg-surface-field py-10 text-ink-secondary transition-colors hover:bg-surface-field focus:outline-none',
  ),
  dragging: 'border-accent-ink bg-accent-tint text-accent-ink',
  dropIcon: 'text-2xl',
  dropTitle: 'text-sm font-semibold text-ink-primary',
  dropHint: 'text-xs text-ink-tertiary',
  list: 'space-y-2',
  item: 'flex items-center gap-3 rounded-xl bg-surface-field px-3 py-2.5',
  name: 'flex-1 truncate text-sm text-ink-primary',
  remove:
    'flex-none flex h-7 w-7 items-center justify-center rounded-full text-ink-tertiary hover:bg-ink-secondary/15 hover:text-ink-primary',
  addMore:
    'w-full rounded-xl border-2 border-dashed border-stroke py-2 text-sm text-ink-tertiary hover:bg-surface-field focus:outline-none',
  validationError: 'whitespace-pre-line text-sm text-mf-red-600',
  caption:
    'block w-full bg-transparent resize-none border-0 px-0 py-0 text-base text-ink-primary placeholder-ink-tertiary focus:ring-0',
};
